import { API_BASE_URL } from "../Api/apiconnector";

class ApiHandlerSuperCoordinator {
  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  // ------------------------------
  // CSRF token fetcher for React
  // ------------------------------
  getCookie(name) {
    let cookieValue = null;
    if (document.cookie) {
      const cookies = document.cookie.split(";").map(c => c.trim());
      for (let cookie of cookies) {
        if (cookie.startsWith(name + "=")) {
          cookieValue = decodeURIComponent(cookie.slice(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  // ---------------------------------------------
  // Core Request Handler (React friendly)
  // ---------------------------------------------
  async makeRequest(endpoint, options = {}, includeCredentials = true) {
    const csrfToken = this.getCookie("csrftoken");

    const finalOptions = {
      method: options.method || "GET",
      headers: {
        ...(options.body && { "Content-Type": "application/json" }),
        ...(csrfToken && { "X-CSRFToken": csrfToken }),
        ...options.headers,
      },
      ...(includeCredentials && { credentials: "include" }),
      ...(options.body && { body: options.body }),
    };

    try {
      const res = await fetch(`${this.baseUrl}${endpoint}`, finalOptions);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      // If response is empty (204 No Content)
      const text = await res.text();
      if (!text) return {};

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      console.error("API Error:", err);
      throw err;
    }
  }

  // ------------------------------ CRUD ------------------------------

  async createFAQ(faqData) {
    return this.makeRequest("/faqs/create/", {
      method: "POST",
      body: JSON.stringify(faqData),
    });
  }

  async getAllClients() {
    return this.makeRequest("/supercoordinator/clients/");
  }

  async createClient(clientData) {
    return this.makeRequest("/supercoordinator/clients/", {
      method: "POST",
      body: JSON.stringify(clientData),
    });
  }

  async updateClient(clientId, clientData) {
    return this.makeRequest(`/supercoordinator/clients/${clientId}/`, {
      method: "PATCH",
      body: JSON.stringify(clientData),
    });
  }

  async getAllRadiologists() {
    return this.makeRequest("/supercoordinator/radiologists/");
  }

  async getAllInstitutions() {
    return this.makeRequest("/supercoordinator/institutions/");
  }

  async getAllModalities() {
    return this.makeRequest("/supercoordinator/modalities/");
  }

  async getAllServices() {
    return this.makeRequest("/supercoordinator/services/");
  }

  async getServiceTATSettings() {
    return this.makeRequest("/supercoordinator/service-tat-settings/");
  }

  async createServiceTATSetting(tatData) {
    return this.makeRequest("/supercoordinator/service-tat-settings/", {
      method: "POST",
      body: JSON.stringify(tatData),
    });
  }

  async updateServiceTATSetting(tatId, tatData) {
    return this.makeRequest(`/supercoordinator/service-tat-settings/${tatId}/`, {
      method: "PATCH",
      body: JSON.stringify(tatData),
    });
  }

  async getPatients(filterParams = {}) {
      const params = new URLSearchParams(filterParams);
      return this.makeRequest(`/supercoordinator/patients/?${params.toString()}`);
  }

  // exportPatients(queryString) {
  //   // The query string should be appended to the URL
  //   return fetch(`${this.baseUrl}/supercoordinator/patients/?${queryString}`, {
  //     method: 'GET',
  //     headers: {
  //       'Content-Type': 'application/json',
  //       // Add authorization headers if needed
  //     }
  //   })
  //   .then(response => {
  //     if (response.ok) {
  //       // Handle file download
  //       return response.blob().then(blob => {
  //         const url = window.URL.createObjectURL(blob);
  //         const a = document.createElement('a');
  //         a.href = url;
  //         a.download = 'patients_export.xlsx';
  //         a.click();
  //         window.URL.revokeObjectURL(url);
  //       });
  //     } else {
  //       return response.json().then(err => { throw err; });
  //     }
  //   });
  // }

  exportPatients(queryString) {
    // The query string should be appended to the URL
    return fetch(`${this.baseUrl}/supercoordinator/patients/?${queryString}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    })
    .then(response => {
      if (response.ok) {
        // Handle file download
        return response.blob().then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'patients_export.xlsx';
          a.click();
          window.URL.revokeObjectURL(url);
        });
      } else {
        return response.json().then(err => { throw err; });
      }
    });
  }

  async downloadExcelFile(filterParams = {}) {
  // Prepare URLSearchParams so multi-select values are preserved
    const params = new URLSearchParams();
  
    // Add export flag
    params.append("export", 1);
  
    Object.entries(filterParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(`${key}[]`, v));
      } else if (value !== "" && value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
  
    const csrfToken = this.getCookie("csrftoken");
  
    const response = await fetch(`${this.baseUrl}/supercoordinator/patients/`, {
      method: "POST",
      headers: {
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: params,  // Send as form data
    });
  
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  
    const blob = await response.blob();
    const contentDisposition = response.headers.get("content-disposition");
    let filename = "patients.xlsx";
  
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match) filename = match[1];
    }
  
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }


  async downloadBillingExcelFile(filterParams = {}) {
  const params = new URLSearchParams();

  // Billing Export
  params.append("export", 2);

  Object.entries(filterParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(v => params.append(`${key}[]`, v));
    } else if (value !== "" && value !== null && value !== undefined) {
      params.append(key, value);
    }
  });

  const csrfToken = this.getCookie("csrftoken");

  const response = await fetch(
    `${this.baseUrl}/supercoordinator/patients/`,
    {
      method: "POST",
      headers: {
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: params,
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const blob = await response.blob();

  const contentDisposition = response.headers.get(
    "content-disposition"
  );

  let filename = "patients_billing.xlsx";

  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename="?(.+)"?/
    );
    if (match) filename = match[1];
  }

  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
  a.remove();
}

async downloadDoctorBillingExcelFile(filterParams = {}) {
  const params = new URLSearchParams();

  // Doctor Billing Export
  params.append("export", 3);

  Object.entries(filterParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v));
    } else if (
      value !== "" &&
      value !== null &&
      value !== undefined
    ) {
      params.append(key, value);
    }
  });

  const csrfToken = this.getCookie("csrftoken");

  const response = await fetch(
    `${this.baseUrl}/supercoordinator/patients/`,
    {
      method: "POST",
      headers: {
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: params,
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const blob = await response.blob();

  const contentDisposition =
    response.headers.get("content-disposition");

  let filename = "doctor_billing.xlsx";

  if (contentDisposition) {
    const match =
      contentDisposition.match(/filename="?(.+)"?/);

    if (match) {
      filename = match[1];
    }
  }

  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
  a.remove();
}


  async getFilteredPatientsForExport(filterParams = {}) {
    const params = new URLSearchParams({ ...filterParams, export: 1 });
    const response = await this.makeRequest(`/supercoordinator/patients/?${params.toString()}`);

    if (response.results) return response.results;
    if (Array.isArray(response)) return response;
    return [];
  }

  generateAndDownloadCSV(data, filterParams = {}) {
    if (!data.length) {
      alert("No data available for export");
      return;
    }

    const csv = this.convertToCSV(data);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    const filename = this.generateFileName(filterParams);

    a.href = url;
    a.download = filename;

    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
    a.remove();
  }

  convertToCSV(data) {
    const headers = Object.keys(data[0]);
    let csv = headers.join(",") + "\n";

    data.forEach(row => {
      const line = headers
        .map(h => {
          const value = row[h] ?? "";
          if (typeof value === "string" && (value.includes(",") || value.includes("\""))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(",");
      csv += line + "\n";
    });

    return csv;
  }

  generateFileName(filterParams = {}) {
    const date = new Date().toISOString().split("T")[0];
    return `patient_records_${date}.csv`;
  }

  async getClientNames() {
    return this.makeRequest("/supercoordinator/clientss/");
  }

  async calculateBilling(patients) {
  try {
    const response = await fetch('https://backend.reportingbot.in/api/calculate_xray_price/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.getCookie('csrftoken'),
      },
      body: JSON.stringify(patients), // ✅ send array directly
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to calculate billing');
    }

    return data;
  } catch (error) {
    console.error('Error in calculateBilling:', error);
    throw error;
  }
}

async doctorBilling(patients) {
  try {
    const response = await fetch('https://backend.reportingbot.in/api/calculate_doctor_price/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': this.getCookie('csrftoken'),
      },
      body: JSON.stringify(patients), // ✅ send array directly
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to calculate billing');
    }

    return data;
  } catch (error) {
    console.error('Error in calculateBilling:', error);
    throw error;
  }
}

// Helper method to get CSRF token if needed
getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}
}

export default ApiHandlerSuperCoordinator;
